"use client"

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react"
import {
  buildFertilizerPlan,
  F_GRASSES,
  F_TYPES,
  F_SEASONS,
  F_GOALS,
  F_SOILS,
  FERT_GRASS_NAME_TO_ID,
  type FertilizerPlan,
  type FertGrassId,
  type FertTypeId,
  type FertSeasonId,
  type FertGoalId,
  type FertSoilId,
  type FertEngineGoal,
} from "@/lib/fertilizer-plan"
import { setBottomDockHeight, useAdDockInset } from "@/hooks/use-bottom-dock-inset"
import { getCurrentSeason } from "@/hooks/use-default-lawn"
import { useCalculatorAnalytics } from "@/hooks/useCalculatorAnalytics"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { getRegionFromZipCode, REGION_DEFAULT_GRASS, toLegacyRegion } from "@/data/regional-climate"
import {
  calculateFertilizerRecommendations,
  type FertilizerResult,
} from "@/lib/fertilizer-calculator"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { FertilizerPreview } from "@/components/tools/fertilizer-preview"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { grassDisplayName } from "@/lib/grass-display"
import { analytics, trackEvent } from "@/lib/analytics"

const F_BG = "#ffffff"
const F_BG_ALT = "#f9fafb"
const F_INK = "#111827"
const F_INK2 = "#374151"
const F_RULE = "#e5e7eb"
const F_BRAND = "#16a34a"
const F_BRAND_700 = "#15803d"
const F_BRAND_50 = "#f0fdf4"
const F_BRAND_100 = "#dcfce7"
const F_MUTED = "#6b7280"
const F_MUTED2 = "#9ca3af"
const F_PAPER_DEEP = "#f3f4f6" // gray-100 — faint backdrop behind the carded sections
const F_FONT = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`

// ── Inline icons ──────────────────────────────────────────────────
function Ico({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoLawn = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M4 20h16" />
    <path d="M7 20c0-4 1-7 3-9" />
    <path d="M12 20c0-5 1-8 3-10" />
    <path d="M17 20c0-3 0-6 2-8" />
  </Ico>
)
const IcoLeaf = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 19c8 0 14-6 14-14C9 5 5 11 5 19z" />
    <path d="M5 19l9-9" />
  </Ico>
)
const IcoFlask = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M9 3h6M10 3v7l-5 9a1 1 0 001 1h12a1 1 0 001-1l-5-9V3" />
  </Ico>
)
const IcoSun = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" />
  </Ico>
)
const IcoTarget = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </Ico>
)
const IcoLayers = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 3l9 5-9 5-9-5 9-5z" />
    <path d="M3 13l9 5 9-5" />
    <path d="M3 18l9 5 9-5" />
  </Ico>
)
const IcoPin = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" />
    <circle cx="12" cy="9" r="2.5" />
  </Ico>
)
const IcoChev = (p: { size?: number }) => <Ico {...p}><path d="M6 9l6 6 6-6" /></Ico>
const IcoArrow = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </Ico>
)
const IcoEdit = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M4 20h4l10-10-4-4L4 16v4z" />
    <path d="M14 6l4 4" />
  </Ico>
)
const IcoLock = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </Ico>
)
const IcoGear = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
  </Ico>
)
const IcoBell = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M6 8a6 6 0 0112 0c0 7 3 8 3 8H3s3-1 3-8z" />
    <path d="M10 21a2 2 0 004 0" />
  </Ico>
)
const IcoCheck = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 12l4 4 10-10" />
  </Ico>
)

// ── Glyphs ────────────────────────────────────────────────────────
function GrassGlyph({ active, season }: { active: boolean; season: "warm" | "cool" }) {
  const ink = active ? F_BRAND : F_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? F_BRAND_100 : F_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ink}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 21h16" />
        <path d="M7 21c0-5 1-8 3-10" />
        <path d="M12 21c0-6 1-9 3-12" />
        <path d="M17 21c0-4 0-7 2-9" />
        {season === "cool" && <circle cx="6" cy="9" r="1.2" fill={ink} stroke="none" />}
      </svg>
    </span>
  )
}

function NPKSwatch({ npk, active }: { npk: string; active: boolean }) {
  const [n, p, k] = npk.split("-").map(x => parseInt(x, 10))
  const max = Math.max(n, p, k, 1)
  const bars = [
    { letter: "N", v: n, color: "#22c55e" },
    { letter: "P", v: p, color: "#a855f7" },
    { letter: "K", v: k, color: "#f59e0b" },
  ]
  return (
    <span
      style={{
        width: 56,
        height: 44,
        borderRadius: 6,
        background: active ? "#fff" : F_BG_ALT,
        border: `1px solid ${active ? F_BRAND : F_RULE}`,
        display: "inline-flex",
        alignItems: "flex-end",
        justifyContent: "space-around",
        padding: "4px 5px",
        flexShrink: 0,
      }}
    >
      {bars.map(b => (
        <span
          key={b.letter}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            height: "100%",
            justifyContent: "flex-end",
          }}
        >
          <span
            style={{
              width: 8,
              height: Math.max(2, (b.v / max) * 26),
              background: b.color,
              borderRadius: 1,
            }}
          />
          <span style={{ fontSize: 8, fontWeight: 700, color: F_MUTED }}>{b.letter}</span>
        </span>
      ))}
    </span>
  )
}

// ── Section primitives ────────────────────────────────────────────
function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: accent ? F_BRAND_50 : F_PAPER_DEEP,
        border: accent ? `1.5px solid ${F_BRAND}` : `1px solid ${F_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${F_BRAND}22` : "none",
        color: accent ? F_BRAND_700 : F_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  icon,
  children,
  hint,
  step,
  accent,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: F_BRAND_700,
          }}
        >
          {icon}
          {children}
        </div>
      </div>
      {hint && (
        <div style={{ fontSize: 13, color: F_MUTED, textAlign: "right" }}>{hint}</div>
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${F_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

// ── Lawn size ─────────────────────────────────────────────────────
const SIZE_PRESETS: Array<{ value: number; label: string; note: string }> = [
  { value: 1500, label: "Small", note: "townhouse / patio" },
  { value: 5000, label: "Typical", note: "suburban lot" },
  { value: 10000, label: "Large", note: "¼-acre lot" },
  { value: 22000, label: "Half ac", note: "half-acre +" },
]

function SizeField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))
  useEffect(() => {
    if (!editing) setDraft(String(value))
  }, [value, editing])

  const min = 200
  const max = 30000
  const toSlider = (v: number) =>
    Math.round(((Math.log(v) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 1000)
  const fromSlider = (s: number) =>
    Math.round(Math.exp(Math.log(min) + (s / 1000) * (Math.log(max) - Math.log(min))) / 50) * 50

  return (
    <div>
      <Label step={2} icon={<IcoLawn size={13} />}>Lawn size</Label>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        {editing ? (
          <input
            autoFocus
            inputMode="numeric"
            aria-label="Lawn size in square feet"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => {
              const n = Math.max(min, Math.min(max, parseInt(draft || "0", 10)))
              onChange(n)
              setEditing(false)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur()
            }}
            style={{
              fontFamily: F_FONT,
              fontSize: 32,
              fontWeight: 700,
              color: F_INK,
              border: "none",
              borderBottom: `2px solid ${F_BRAND}`,
              background: "transparent",
              width: 180,
              padding: "2px 0",
              minHeight: 44,
              letterSpacing: "-0.025em",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Lawn size ${value} square feet, click to edit`}
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 8,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "text",
              color: F_INK,
              textAlign: "left",
            }}
          >
            <span
              style={{
                fontFamily: F_FONT,
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "-0.025em",
              }}
            >
              {value.toLocaleString()}
            </span>
            <span style={{ fontSize: 13, color: F_MUTED }}>sq ft</span>
            <span style={{ color: F_MUTED }}>
              <IcoEdit size={13} />
            </span>
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: F_MUTED }}>
          ≈ {(value / 43560).toFixed(value < 5000 ? 3 : 2)} ac
        </span>
      </div>

      <div style={{ position: "relative", padding: "6px 0 14px" }}>
        <input
          type="range"
          min={0}
          max={1000}
          aria-label="Lawn size slider"
          aria-valuetext={`${value.toLocaleString()} square feet`}
          value={toSlider(value)}
          onChange={(e) => onChange(fromSlider(parseInt(e.target.value, 10)))}
          className="fert-range"
          style={{
            width: "100%",
            height: 6,
            WebkitAppearance: "none",
            appearance: "none",
            background: `linear-gradient(to right, ${F_BRAND} 0%, ${F_BRAND} ${toSlider(value) / 10}%, ${F_RULE} ${toSlider(value) / 10}%, ${F_RULE} 100%)`,
            borderRadius: 3,
            cursor: "pointer",
          }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {SIZE_PRESETS.map((p) => {
          const active = Math.abs(p.value - value) < 200
          return (
            <button
              type="button"
              key={p.value}
              onClick={() => onChange(p.value)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? F_BRAND_50 : "#fff",
                color: F_INK,
                border: `1px solid ${active ? F_BRAND : F_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.label}</div>
              <div style={{ fontSize: 11.5, color: F_MUTED, marginTop: 2 }}>
                {p.value.toLocaleString()} · {p.note}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Grass ────────────────────────────────────────────────────────
const GRASS_RECOMMENDED: FertGrassId[] = ["bermuda", "zoysia", "fescue", "kbg"]

function GrassField({
  value,
  onChange,
}: {
  value: FertGrassId
  onChange: (v: FertGrassId) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const all = Object.keys(F_GRASSES) as FertGrassId[]
  const list = showAll ? all : GRASS_RECOMMENDED
  return (
    <div>
      <Label step={3} icon={<IcoLeaf size={13} />} hint={`${F_GRASSES[value].annualN} lb N / yr`}>
        Grass type
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {list.map((id) => {
          const g = F_GRASSES[id]
          const active = id === value
          return (
            <button
              type="button"
              key={id}
              onClick={() => onChange(id)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? F_BRAND_50 : "#fff",
                color: F_INK,
                border: `1px solid ${active ? F_BRAND : F_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span className="hidden xs:inline-flex">
                <GrassGlyph active={active} season={g.season} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    overflowWrap: "break-word",
                  }}
                >
                  {grassDisplayName(g.name)}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: F_MUTED,
                    marginTop: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {g.tag} · {g.annualN} lb N
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {!showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            marginTop: 10,
            background: "transparent",
            border: "none",
            padding: "4px 0",
            minHeight: 44,
            fontSize: 13,
            color: "var(--color-action)",
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          +{all.length - GRASS_RECOMMENDED.length} more grass types <IcoChev size={12} />
        </button>
      )}
    </div>
  )
}

// ── Fertilizer type ──────────────────────────────────────────────
function TypeField({
  value,
  onChange,
}: {
  value: FertTypeId
  onChange: (v: FertTypeId) => void
}) {
  return (
    <div>
      <Label step={4} icon={<IcoFlask size={13} />} hint={F_TYPES[value].npk}>
        Fertilizer type
      </Label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(Object.entries(F_TYPES) as [FertTypeId, (typeof F_TYPES)[FertTypeId]][]).map(
          ([id, t]) => {
            const active = id === value
            return (
              <button
                type="button"
                key={id}
                onClick={() => onChange(id)}
                aria-pressed={active}
                style={{
                  padding: "10px 12px",
                  background: active ? F_BRAND_50 : "#fff",
                  color: F_INK,
                  border: `1px solid ${active ? F_BRAND : F_RULE}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <NPKSwatch npk={t.npk} active={active} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: F_MUTED,
                        background: F_BG_ALT,
                        padding: "2px 6px",
                        borderRadius: 4,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {t.npk}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: F_MUTED, marginTop: 2 }}>{t.blurb}</div>
                </div>
              </button>
            )
          },
        )}
      </div>
    </div>
  )
}

// ── Season ────────────────────────────────────────────────────────
function SeasonField({
  value,
  onChange,
}: {
  value: FertSeasonId
  onChange: (v: FertSeasonId) => void
}) {
  return (
    <div>
      <Label step={5} icon={<IcoSun size={13} />} hint="defaults to today">
        Application season
      </Label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          background: "#fff",
          border: `1px solid ${F_RULE}`,
          borderRadius: 8,
          padding: 4,
          gap: 4,
        }}
      >
        {F_SEASONS.map((s) => {
          const active = s.id === value
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onChange(s.id)}
              aria-pressed={active}
              style={{
                padding: "10px 6px",
                background: active ? F_BRAND_700 : "transparent",
                color: active ? "#fff" : F_INK,
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {s.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function FertilizerPreviewPrompt({ compact = false }: { compact?: boolean }) {
  return (
    <div
      role="status"
      style={{
        background: "#fff",
        borderRadius: compact ? 12 : 16,
        border: `1px solid ${F_RULE}`,
        boxShadow: compact
          ? "0 6px 20px rgba(0,0,0,0.04)"
          : "0 12px 40px rgba(0,0,0,0.04)",
        padding: compact ? 16 : 24,
        color: F_INK,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
          borderRadius: 9999,
          background: F_BRAND_50,
          color: F_BRAND_700,
          marginBottom: 10,
        }}
      >
        <IcoPin size={16} />
      </div>
      <div style={{ fontSize: compact ? 15 : 17, fontWeight: 700, marginBottom: 4 }}>
        Add your ZIP to calculate your amount
      </div>
      <p style={{ margin: 0, color: F_MUTED, fontSize: 13, lineHeight: 1.5 }}>
        Your fertilizer pounds, bag count, and feeding calendar will appear here after we match
        your climate zone.
      </p>
    </div>
  )
}

// ── Goal ──────────────────────────────────────────────────────────
function GoalField({
  value,
  onChange,
}: {
  value: FertGoalId
  onChange: (v: FertGoalId) => void
}) {
  return (
    <div>
      <Label step={6} icon={<IcoTarget size={13} />}>Goal</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {(Object.entries(F_GOALS) as [FertGoalId, (typeof F_GOALS)[FertGoalId]][]).map(
          ([id, g]) => {
            const active = id === value
            return (
              <button
                type="button"
                key={id}
                onClick={() => onChange(id)}
                aria-pressed={active}
                style={{
                  padding: "12px 14px",
                  background: active ? F_BRAND_50 : "#fff",
                  color: F_INK,
                  border: `1px solid ${active ? F_BRAND : F_RULE}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600 }}>{g.name}</div>
                <div style={{ fontSize: 12, color: F_MUTED, marginTop: 2 }}>{g.blurb}</div>
              </button>
            )
          },
        )}
      </div>
    </div>
  )
}

// ── Soil ──────────────────────────────────────────────────────────
function SoilField({
  value,
  onChange,
}: {
  value: FertSoilId
  onChange: (v: FertSoilId) => void
}) {
  return (
    <div>
      <Label step={7} icon={<IcoLayers size={13} />} hint={F_SOILS[value].blurb}>
        Soil type
      </Label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(Object.entries(F_SOILS) as [FertSoilId, (typeof F_SOILS)[FertSoilId]][]).map(
          ([id, s]) => {
            const active = id === value
            return (
              <button
                type="button"
                key={id}
                onClick={() => onChange(id)}
                aria-pressed={active}
                style={{
                  padding: "8px 14px",
                  background: active ? F_BRAND_700 : "#fff",
                  color: active ? "#fff" : F_INK2,
                  border: `1px solid ${active ? F_BRAND : F_RULE}`,
                  borderRadius: 9999,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {s.name}
              </button>
            )
          },
        )}
      </div>
    </div>
  )
}

// ── Form ──────────────────────────────────────────────────────────
const F_REGION_TO_GRASS: Record<string, FertGrassId> = {
  Southeast: "bermuda",
  "South Central": "bermuda",
  Northeast: "fescue",
  "Great Lakes": "kbg",
  "Upper Midwest": "kbg",
  "Mountain West": "kbg",
  "Mid-Atlantic": "fescue",
  Central: "fescue",
  "West Coast": "fescue",
}

const SPREADER_SETTINGS = [
  { brand: "Scotts broadcast", setting: "3 to 4", note: "Start one notch low" },
  { brand: "Drop spreader", setting: "4 to 5", note: "Overlap wheel tracks" },
  { brand: "Earthway rotary", setting: "17 to 19", note: "Use two half-rate passes" },
]

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type ReminderStatus = "idle" | "submitting" | "success" | "error"

function getNextFeedingLabel(plan: FertilizerPlan) {
  const today = new Date()
  const currentMonth = today.getMonth()
  const nextThisYear = plan.months.find((month) => month.on && month.i > currentMonth)
  const nextMonth = nextThisYear ?? plan.months.find((month) => month.on)

  return nextMonth ? nextMonth.label : "next season"
}

function RailCard({
  children,
  accent = false,
}: {
  children: React.ReactNode
  accent?: boolean
}) {
  return (
    <section
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${accent ? F_BRAND_100 : F_RULE}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.03)",
        padding: "18px 20px",
      }}
    >
      {children}
    </section>
  )
}

function RailKicker({
  icon,
  children,
  right,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  right?: React.ReactNode
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: F_BRAND_700,
        }}
      >
        {icon}
        {children}
      </div>
      {right}
    </div>
  )
}

function SpreaderSettingsCard({ plan }: { plan: FertilizerPlan }) {
  return (
    <RailCard>
      <RailKicker
        icon={<IcoGear size={12} />}
        right={<span style={{ fontSize: 11, color: F_MUTED2 }}>{plan.type.npk}</span>}
      >
        Spreader starting points
      </RailKicker>
      <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5, color: F_MUTED }}>
        Use the bag label when it lists your model. Otherwise, start here and calibrate on a
        driveway before applying.
      </p>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {SPREADER_SETTINGS.map((setting, index) => (
          <div
            key={setting.brand}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 0",
              borderTop: index === 0 ? "none" : `1px solid ${F_RULE}`,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: F_INK }}>
                {setting.brand}
              </div>
              <div style={{ fontSize: 11.5, color: F_MUTED, marginTop: 1 }}>{setting.note}</div>
            </div>
            <div
              style={{
                minWidth: 62,
                padding: "6px 9px",
                background: F_BRAND_50,
                color: F_BRAND_700,
                border: `1px solid ${F_BRAND_100}`,
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              {setting.setting}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 8,
          fontSize: 12,
          lineHeight: 1.5,
          color: F_MUTED,
        }}
      >
        <span style={{ flexShrink: 0, color: F_BRAND_700 }}>
          <IcoCheck size={12} />
        </span>
        <span>
          For {plan.productLbs.toFixed(1)} lbs total, use two passes at half rate for the most even
          coverage.
        </span>
      </div>
    </RailCard>
  )
}

function RailProductRecommendation({
  keywords,
}: {
  keywords: string[]
}) {
  return (
    <ProductRecommendations
      categories={["Fertilizers & Soil Health"]}
      limit={1}
      title="Recommended bag"
      trackingContext="fertilizer-calculator-rail"
      keywords={keywords}
      compact
    />
  )
}

function ReminderCard({
  nextFeeding,
  email,
  status,
  error,
  onEmailChange,
  onSubmit,
}: {
  nextFeeding: string
  email: string
  status: ReminderStatus
  error: string | null
  onEmailChange: (value: string) => void
  onSubmit: () => void
}) {
  if (status === "success") {
    return (
      <RailCard accent>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              background: F_BRAND_100,
              color: F_BRAND_700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IcoCheck size={15} />
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: F_INK }}>
              You're on the list
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.5, color: F_MUTED }}>
              We'll send fertilizer timing tips before your next feeding window.
            </p>
          </div>
        </div>
      </RailCard>
    )
  }

  return (
    <RailCard accent>
      <RailKicker icon={<IcoBell size={12} />}>Set a reminder</RailKicker>
      <p style={{ margin: "0 0 12px", fontSize: 13.5, lineHeight: 1.5, color: F_INK2 }}>
        Get a heads-up before your next feeding window in <strong>{nextFeeding}</strong>.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="you@email.com"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              onSubmit()
            }
          }}
          aria-label="Email for fertilizer reminder"
          aria-invalid={status === "error"}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "10px 12px",
            border: `1px solid ${status === "error" ? "#fca5a5" : F_RULE}`,
            borderRadius: 8,
            fontSize: 16,
            color: F_INK,
            background: "#fff",
          }}
        />
        <button
          type="button"
          disabled={status === "submitting"}
          onClick={onSubmit}
          style={{
            padding: "10px 13px",
            background: status === "submitting" ? "#9ca3af" : F_BRAND,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: status === "submitting" ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {status === "submitting" ? "Saving" : "Remind me"}
        </button>
      </div>
      {status === "error" && error && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#dc2626" }} role="alert">
          {error}
        </p>
      )}
      <div
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11.5,
          lineHeight: 1.45,
          color: F_MUTED,
        }}
      >
        <IcoLock size={11} />
        <span>Free monthly timing tips. Unsubscribe anytime.</span>
      </div>
    </RailCard>
  )
}

export interface FertilizerFormSubmitData {
  zipCode: string
  lawnSize: number
  grassType: string
  sunlight?: string
  fertilizerType: string
  season: FertSeasonId
  applicationGoal: FertEngineGoal
  soilType: "clay" | "sandy" | "loamy" | "unknown"
}

export interface FertilizerFormRef {
  reset: () => void
}

interface FertilizerFormProps {
  initialValues?: {
    zipCode?: string
    lawnSize?: string | number
    grassType?: string
    sunlight?: string
    fertilizerType?: string
    season?: string
    applicationGoal?: string
    soilType?: string
  }
  // Optional. When provided, the form renders a submit button and calls
  // this on submit. When omitted (the default for the canonical tool page),
  // the form is fully live-only with no submit gate. Used by the embed
  // widget which still wants a submit-then-results flow.
  onSubmit?: (data: FertilizerFormSubmitData) => void
  isLoading?: boolean
  error?: string | null
  /** Called whenever the live engine result changes, so the page can render
   * downstream UI (like the data chart) without duplicating the engine call. */
  onResultChange?: (result: FertilizerResult | null) => void
}

type FertilizerInitialValues = NonNullable<FertilizerFormProps["initialValues"]>

const VALID_FERT_SEASONS: FertSeasonId[] = ["spring", "summer", "fall", "winter"]

const FERT_TYPE_ALIASES: Record<string, FertTypeId> = {
  synthetic: "balanced",
  "high-nitrogen": "highN",
  "slow-release": "organic",
  "slow-release-15-5-10": "organic",
}

function parseInitialSqft(value: FertilizerInitialValues["lawnSize"]): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value
  if (typeof value === "string") {
    const parsed = parseInt(value, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  return null
}

function parseInitialGrass(value?: string): FertGrassId | null {
  return value && FERT_GRASS_NAME_TO_ID[value] ? FERT_GRASS_NAME_TO_ID[value] : null
}

function parseInitialType(value?: string): FertTypeId | null {
  if (!value) return null
  const normalized = value.trim()
  if (normalized in F_TYPES) return normalized as FertTypeId
  const alias = FERT_TYPE_ALIASES[normalized.toLowerCase()]
  if (alias) return alias
  const match = Object.entries(F_TYPES).find(([, def]) => def.engineKey === normalized)
  return match ? (match[0] as FertTypeId) : null
}

function parseInitialSeason(value?: string): FertSeasonId | null {
  return value && VALID_FERT_SEASONS.includes(value as FertSeasonId)
    ? (value as FertSeasonId)
    : null
}

function parseInitialGoal(value?: string): FertGoalId | null {
  if (!value) return null
  if (value in F_GOALS) return value as FertGoalId
  const match = Object.entries(F_GOALS).find(([, def]) => def.engineKey === value)
  return match ? (match[0] as FertGoalId) : null
}

function parseInitialSoil(value?: string): FertSoilId | null {
  if (!value) return null
  if (value in F_SOILS) return value as FertSoilId
  const match = Object.entries(F_SOILS).find(([, def]) => def.engineKey === value)
  return match ? (match[0] as FertSoilId) : null
}

export const FertilizerForm = forwardRef<FertilizerFormRef, FertilizerFormProps>(
  function FertilizerForm({ initialValues, onSubmit, isLoading = false, error = null, onResultChange }, ref) {
    const [zip, setZip] = useState("")
    const [sqft, setSqft] = useState<number>(() => {
      return parseInitialSqft(initialValues?.lawnSize) ?? 5000
    })
    const [grass, setGrass] = useState<FertGrassId>(() => {
      return parseInitialGrass(initialValues?.grassType) ?? "bermuda"
    })
    const [type, setType] = useState<FertTypeId>(() => {
      return parseInitialType(initialValues?.fertilizerType) ?? "balanced"
    })
    const [season, setSeason] = useState<FertSeasonId>(() => {
      return parseInitialSeason(initialValues?.season) ?? getCurrentSeason()
    })
    const [goal, setGoal] = useState<FertGoalId>(() => {
      return parseInitialGoal(initialValues?.applicationGoal) ?? "maintain"
    })
    const [soil, setSoil] = useState<FertSoilId>(() => {
      return parseInitialSoil(initialValues?.soilType) ?? "loam"
    })

    useImperativeHandle(ref, () => ({
      reset() {
        setZip("")
        setSqft(5000)
        setGrass("bermuda")
        setType("balanced")
        setSeason(getCurrentSeason())
        setGoal("maintain")
        setSoil("loam")
      },
    }))

    const region = useMemo(() => getRegionFromZipCode(zip), [zip])
    const zipKnown = zip.length === 5 && region !== "Unknown"
    const zipError = zip.length === 5 && !zipKnown
      ? "We couldn't match that ZIP code. Check it and try again."
      : undefined

    const { savedInputs, saveInputs } = useSavedInputs()
    const seededFromZip = useRef(false)
    const seededFromSaved = useRef(false)
    const seededSizeFromSaved = useRef(false)
    const regionalGrass = useMemo<FertGrassId | null>(() => {
      if (!zipKnown) return null
      const defaultGrass = REGION_DEFAULT_GRASS[region]
      if (defaultGrass && FERT_GRASS_NAME_TO_ID[defaultGrass]) {
        return FERT_GRASS_NAME_TO_ID[defaultGrass]
      }
      return F_REGION_TO_GRASS[toLegacyRegion(region)] ?? null
    }, [region, zipKnown])

    // ZIP validity changes during render, before the seeding effect below can
    // commit its state update. Use the resolved regional grass immediately so
    // no preview, analytics event, or parent result can expose the old default.
    const effectiveGrass = zipKnown && !seededFromZip.current && regionalGrass
      ? regionalGrass
      : grass

    useEffect(() => {
      if (!initialValues) return

      if (initialValues.zipCode && /^\d{5}$/.test(initialValues.zipCode)) {
        setZip(initialValues.zipCode)
      }

      const nextSqft = parseInitialSqft(initialValues.lawnSize)
      if (nextSqft !== null) {
        setSqft(nextSqft)
        seededSizeFromSaved.current = true
      }

      const nextGrass = parseInitialGrass(initialValues.grassType)
      if (nextGrass) {
        setGrass(nextGrass)
        seededFromZip.current = true
        seededFromSaved.current = true
      }

      const nextType = parseInitialType(initialValues.fertilizerType)
      if (nextType) setType(nextType)

      const nextSeason = parseInitialSeason(initialValues.season)
      if (nextSeason) setSeason(nextSeason)

      const nextGoal = parseInitialGoal(initialValues.applicationGoal)
      if (nextGoal) setGoal(nextGoal)

      const nextSoil = parseInitialSoil(initialValues.soilType)
      if (nextSoil) setSoil(nextSoil)
    }, [initialValues])

    // Cross-site savedInputs takes precedence over zip-derived defaults.
    // Skip if initialValues already supplied a value (URL params win).
    useEffect(() => {
      if (seededFromSaved.current) return
      if (initialValues?.grassType) {
        seededFromSaved.current = true
        return
      }
      const saved = savedInputs.grassType
      if (saved && FERT_GRASS_NAME_TO_ID[saved]) {
        setGrass(FERT_GRASS_NAME_TO_ID[saved])
        seededFromZip.current = true
        seededFromSaved.current = true
      }
    }, [savedInputs.grassType, initialValues?.grassType])

    useEffect(() => {
      if (seededSizeFromSaved.current) return
      if (initialValues?.lawnSize) {
        seededSizeFromSaved.current = true
        return
      }
      const saved = savedInputs.lawnSize
      if (typeof saved === 'number' && saved > 0) {
        setSqft(saved)
        seededSizeFromSaved.current = true
      }
    }, [savedInputs.lawnSize, initialValues?.lawnSize])

    useEffect(() => {
      if (!zipKnown || seededFromZip.current) return
      if (regionalGrass) setGrass(regionalGrass)
      seededFromZip.current = true
    }, [zipKnown, regionalGrass])

    const plan = useMemo(
      () => buildFertilizerPlan({ sqft, grass: effectiveGrass, type, season, goal, soil }),
      [sqft, effectiveGrass, type, season, goal, soil],
    )

    const liveResult = useMemo<FertilizerResult | null>(() => {
      const sunlight = initialValues?.sunlight ?? savedInputs.sunlight
      try {
        const engineResult = calculateFertilizerRecommendations({
          lawnSize: sqft,
          grassType: F_GRASSES[effectiveGrass].engineKey,
          sunlight,
          fertilizerType: F_TYPES[type].engineKey,
          season,
          applicationGoal: F_GOALS[goal].engineKey,
          soilType: F_SOILS[soil].engineKey as 'clay' | 'sandy' | 'loamy' | 'unknown',
        })

        // The live preview is the primary answer on this page. Keep every
        // downstream result surface on that same plan so users never see one
        // amount in the form rail and a different amount below it.
        const lawnUnits = Math.max(sqft / 1000, 0.001)
        const pounds = Math.round(plan.productLbs * 10) / 10
        const costPerPound = engineResult.fertilizerAmount.costBreakdown?.costPerPound ?? 0
        const costEstimate = Math.round(pounds * costPerPound * 100) / 100

        return {
          ...engineResult,
          npkRequirements: {
            nitrogen: plan.npk.n / lawnUnits,
            phosphorus: plan.npk.p / lawnUnits,
            potassium: plan.npk.k / lawnUnits,
          },
          fertilizerAmount: {
            ...engineResult.fertilizerAmount,
            pounds,
            bags: pounds > 0 ? Math.ceil(plan.bags) : 0,
            bagSize: plan.bagSize,
            costEstimate,
            costPerBag: Math.round(plan.bagSize * costPerPound * 100) / 100,
            costBreakdown: engineResult.fertilizerAmount.costBreakdown
              ? {
                  ...engineResult.fertilizerAmount.costBreakdown,
                  totalCost: costEstimate,
                  costPerPound,
                }
              : undefined,
          },
        }
      } catch {
        return null
      }
    }, [sqft, effectiveGrass, type, season, goal, soil, plan, initialValues?.sunlight, savedInputs.sunlight])

    useEffect(() => {
      onResultChange?.(zipKnown ? liveResult : null)
    }, [liveResult, onResultChange, zipKnown])

    useCalculatorAnalytics(
      'fertilizer',
      zipKnown
        ? {
            inputs: {
              zip_code: zip,
              lawn_size_sqft: sqft,
              grass_type: F_GRASSES[effectiveGrass].engineKey,
              fertilizer_type: F_TYPES[type].engineKey,
              season,
              application_goal: F_GOALS[goal].engineKey,
              soil_type: F_SOILS[soil].engineKey,
            },
            result: {
              region,
              annual_n_lbs: plan.annualNlbs,
              this_app_n_lbs: plan.thisAppNlbs,
              product_lbs: plan.productLbs,
              bags: plan.bags,
              apps_per_year: plan.appsPerYear,
            },
            location: zip,
            source: 'live_update',
          }
        : null,
    )

    // Keep the rail stacked until the form and sticky summary both have room.
    const [narrow, setNarrow] = useState(false)
    const [phone, setPhone] = useState(false)
    const [formInView, setFormInView] = useState(true)
    const formRef = useRef<HTMLFormElement | null>(null)
    const dockRef = useRef<HTMLDivElement | null>(null)
    // Clears the Mediavine anchor/player beneath this bar. Must be the ad-only
    // inset, not the dock total, which includes this bar's own height.
    const adDockInset = useAdDockInset()
    useEffect(() => {
      const mq = window.matchMedia("(max-width: 1199px)")
      const sync = () => setNarrow(mq.matches)
      sync()
      mq.addEventListener("change", sync)
      return () => mq.removeEventListener("change", sync)
    }, [])

    useEffect(() => {
      const mq = window.matchMedia("(max-width: 767px)")
      const sync = () => setPhone(mq.matches)
      sync()
      mq.addEventListener("change", sync)
      return () => mq.removeEventListener("change", sync)
    }, [])

    useEffect(() => {
      if (!phone) {
        setFormInView(true)
        return
      }
      const el = formRef.current
      if (!el || typeof IntersectionObserver === "undefined") return
      const observer = new IntersectionObserver(
        ([entry]) => setFormInView(entry.isIntersecting),
        { rootMargin: "0px 0px -25% 0px", threshold: 0 },
      )
      observer.observe(el)
      return () => observer.disconnect()
    }, [phone])

    const showDock = phone && formInView && Boolean(onSubmit)

    useEffect(() => {
      if (!showDock) {
        setBottomDockHeight(0)
        return
      }
      const el = dockRef.current
      if (!el) return
      const ro = new ResizeObserver(() => {
        setBottomDockHeight(el.getBoundingClientRect().height)
      })
      ro.observe(el)
      setBottomDockHeight(el.getBoundingClientRect().height)
      return () => {
        ro.disconnect()
        setBottomDockHeight(0)
      }
    }, [showDock])

    const nextFeedingLabel = useMemo(() => getNextFeedingLabel(plan), [plan])
    const railProductKeywords = useMemo(
      () => [
        F_GRASSES[effectiveGrass].engineKey,
        F_TYPES[type].name,
        F_TYPES[type].npk,
        season,
      ],
      [effectiveGrass, type, season],
    )
    const showRailProduct = F_GRASSES[effectiveGrass].engineKey !== "Carpetgrass"
    const [reminderEmail, setReminderEmail] = useState("")
    const [reminderStatus, setReminderStatus] = useState<ReminderStatus>("idle")
    const [reminderError, setReminderError] = useState<string | null>(null)

    const handleReminderEmailChange = (value: string) => {
      setReminderEmail(value)
      if (reminderStatus !== "submitting") {
        setReminderStatus("idle")
        setReminderError(null)
      }
    }

    const handleReminderSubmit = async () => {
      if (reminderStatus === "submitting" || reminderStatus === "success") return

      const trimmedEmail = reminderEmail.trim()
      if (!EMAIL_REGEX.test(trimmedEmail)) {
        setReminderStatus("error")
        setReminderError("Please enter a valid email address.")
        return
      }

      setReminderStatus("submitting")
      setReminderError(null)

      try {
        const response = await fetch("/api/email/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmedEmail,
            zipCode: zipKnown ? zip : undefined,
            context: "fertilizer_calculator_right_rail",
          }),
        })

        const payload = await response.json().catch(() => null)
        if (response.ok || response.status === 409 || payload?.error === "already_subscribed") {
          analytics.newsletterSignup(trimmedEmail, "fertilizer_calculator_right_rail")
          trackEvent("fertilizer_reminder_signup", {
            grass_type: F_GRASSES[effectiveGrass].engineKey,
            fertilizer_type: F_TYPES[type].engineKey,
            season,
            zip_prefix: zipKnown ? zip.slice(0, 3) : undefined,
            next_feeding: nextFeedingLabel,
          })
          setReminderStatus("success")
          return
        }

        setReminderStatus("error")
        setReminderError(payload?.error || "Something went wrong. Please try again.")
      } catch {
        setReminderStatus("error")
        setReminderError("Network error. Please try again.")
      }
    }

    // When the parent passed an onSubmit, mirror the legacy submit-then-
    // results behaviour so the embed widget keeps working. Without an
    // onSubmit (the canonical tool page) submit is a no-op and the form is
    // fully live.
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (!onSubmit || !zipKnown) return
      onSubmit({
        zipCode: zip,
        lawnSize: sqft,
        grassType: F_GRASSES[effectiveGrass].engineKey,
        sunlight: initialValues?.sunlight ?? savedInputs.sunlight,
        fertilizerType: F_TYPES[type].engineKey,
        season,
        applicationGoal: F_GOALS[goal].engineKey,
        soilType: F_SOILS[soil].engineKey,
      })
    }

    return (
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        style={{
          background: F_BG,
          color: F_INK,
          fontFamily: F_FONT,
          borderRadius: 18,
          border: `1px solid ${F_RULE}`,
          boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <style>{`
          .fert-range::-webkit-slider-thumb {
            -webkit-appearance: none; appearance: none;
            width: 22px; height: 22px; border-radius: 50%;
            background: #fff; border: 2px solid ${F_BRAND};
            box-shadow: 0 1px 4px rgba(0,0,0,0.15);
            cursor: grab;
          }
          .fert-range::-moz-range-thumb {
            width: 22px; height: 22px; border-radius: 50%;
            background: #fff; border: 2px solid ${F_BRAND};
            cursor: grab;
          }
          .fert-range:focus-visible {
            outline: 3px solid rgba(22, 163, 74, 0.35);
            outline-offset: 5px;
          }
        `}</style>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: narrow ? "minmax(0,1fr)" : "minmax(520px,1fr) minmax(360px,420px)",
            gap: narrow ? 24 : 32,
            padding: narrow
              ? (showDock ? "20px 18px 100px" : "20px 18px 28px")
              : "32px 32px 36px",
          }}
        >
          {/* Mobile: live preview first */}
          {narrow && (zipKnown
            ? <FertilizerPreview plan={plan} compact />
            : <FertilizerPreviewPrompt compact />)}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
              background: F_PAPER_DEEP,
              borderRadius: 14,
              padding: 12,
            }}
          >
            <SectionCard>
              <Label
                step={1}
                accent
                icon={<IcoPin size={13} />}
                hint={zipKnown ? `climate zone matched: ${region}` : "sets your climate zone"}
              >
                Where
              </Label>
              <ZipCodeInput
                value={zip}
                onChange={setZip}
                error={zipError}
                showLabel={false}
                enableGeolocation
                rememberLastZip
              />
            </SectionCard>
            <SectionCard>
              <SizeField
                value={sqft}
                onChange={(n) => {
                  setSqft(n)
                  if (n > 0) saveInputs({ lawnSize: n })
                }}
              />
            </SectionCard>
            <SectionCard>
              <GrassField
                value={effectiveGrass}
                onChange={(g) => {
                  seededFromZip.current = true
                  setGrass(g)
                  saveInputs({ grassType: F_GRASSES[g].engineKey })
                }}
              />
            </SectionCard>
            <SectionCard>
              <TypeField value={type} onChange={setType} />
            </SectionCard>
            <SectionCard>
              <SeasonField value={season} onChange={setSeason} />
            </SectionCard>
            <SectionCard>
              <GoalField value={goal} onChange={setGoal} />
            </SectionCard>
            <SectionCard>
              <SoilField value={soil} onChange={(s) => {
                setSoil(s)
                saveInputs({ soilType: F_SOILS[s].engineKey })
              }} />
            </SectionCard>

            {error && (
              <div
                role="alert"
                style={{
                  fontSize: 13,
                  color: "#b04531",
                  background: "#fff5f3",
                  border: "1px solid #f5d4cd",
                  borderRadius: 8,
                  padding: "10px 12px",
                }}
              >
                {error}
              </div>
            )}
          </div>

          {!narrow && (
            <aside
              style={{
                alignSelf: "flex-start",
                position: "sticky",
                top: 24,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {zipKnown
                ? <FertilizerPreview plan={plan} />
                : <FertilizerPreviewPrompt />}
              {onSubmit && (
                <button
                  type="submit"
                  disabled={isLoading || !zipKnown}
                  style={{
                    padding: "14px 18px",
                    background: zipKnown ? F_BRAND : "#e5e7eb",
                    color: zipKnown ? "#fff" : "#9ca3af",
                    border: "none",
                    borderRadius: 9999,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: zipKnown && !isLoading ? "pointer" : "not-allowed",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: zipKnown ? "0 8px 20px -6px rgba(22,163,74,0.30)" : "none",
                    transition: "background 200ms var(--ease-standard)",
                  }}
                >
                  {isLoading
                    ? "Calculating…"
                    : zipKnown
                      ? "See full schedule"
                      : "Enter your ZIP"}
                  {zipKnown && !isLoading && <IcoArrow size={14} />}
                </button>
              )}
              {!onSubmit && zipKnown && (
                <>
                  {plan.productLbs > 0 && <SpreaderSettingsCard plan={plan} />}
                  {showRailProduct && <RailProductRecommendation keywords={railProductKeywords} />}
                  <ReminderCard
                    nextFeeding={nextFeedingLabel}
                    email={reminderEmail}
                    status={reminderStatus}
                    error={reminderError}
                    onEmailChange={handleReminderEmailChange}
                    onSubmit={handleReminderSubmit}
                  />
                </>
              )}
              <div
                style={{
                  fontSize: 12,
                  color: F_MUTED,
                  lineHeight: 1.55,
                  display: "flex",
                  gap: 8,
                  padding: "0 4px",
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 2 }}>
                  <IcoLock size={12} />
                </span>
                <span>
                  From university extension data, last reviewed May 2026. A soil test gives the
                  most accurate rate.
                </span>
              </div>
            </aside>
          )}
        </div>

        {showDock && (
          <div
            ref={dockRef}
            style={{
              position: "fixed",
              bottom: adDockInset,
              left: 0,
              right: 0,
              zIndex: 30,
              boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
              background: "#fff",
              borderTop: `1px solid ${F_RULE}`,
              padding: "12px 16px",
              paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: F_BRAND_700,
                  marginBottom: 2,
                }}
              >
                {zipKnown ? "Your result" : "Finish setup"}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: F_INK,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {zipKnown
                  ? `${plan.productLbs.toFixed(1)} lbs · ${plan.type.npk}`
                  : "Enter a valid ZIP"}
              </div>
            </div>
            {onSubmit && (
              <button
                type="submit"
                disabled={isLoading || !zipKnown}
                style={{
                  padding: "12px 18px",
                  background: zipKnown ? F_BRAND : "#e5e7eb",
                  color: zipKnown ? "#fff" : "#9ca3af",
                  border: "none",
                  borderRadius: 9999,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: zipKnown && !isLoading ? "pointer" : "not-allowed",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {isLoading ? "Calculating…" : zipKnown ? "See full" : "Enter ZIP"}
                {zipKnown && !isLoading && <IcoArrow size={13} />}
              </button>
            )}
          </div>
        )}
      </form>
    )
  },
)
